<script lang="ts">
	import type { ClassValue } from 'svelte/elements';
	import { clickOutside } from '$lib/actions/clickOutside';
	import Button from '$lib/components/atoms/button/Button.svelte';
	import * as m from '$paraglide/messages';
	import { computePosition, offset, type Placement } from '@floating-ui/dom';
	import { type Snippet, tick } from 'svelte';
	import { on } from 'svelte/events';

	interface Props {
		open?: boolean;
		centered?: boolean;
		hideCloseButton?: boolean;
		closeText?: string;
		relativeTo?: HTMLElement;
		relativeHeightOffset?: number;
		placement?: Placement;
		contentDialogClasses?: ClassValue;
		closeOnClickOutside?: boolean;
		extraStyle?: string;
		closeCallback?: () => void;
		closeButtonCallback?: (e: MouseEvent) => void;
		clickOutsideCallback?: (e: CustomEvent) => void;
		paneClasses?: ClassValue;
		closeButtonSlot?: Snippet;
		children: Snippet;
		class?: ClassValue;
	}
	let {
		open = $bindable(),
		centered = false,
		hideCloseButton = false,
		closeText = m.modal__close(),
		relativeTo,
		relativeHeightOffset = 0,
		placement = 'bottom-end',
		contentDialogClasses = 'max-w-[calc(100vw-16px)]',
		closeOnClickOutside = true,
		extraStyle = '',
		closeCallback,
		closeButtonCallback,
		clickOutsideCallback,
		paneClasses,
		class: classes,
		closeButtonSlot,
		children,
	}: Props = $props();

	let dialog: HTMLDialogElement;
	let contentEl: HTMLElement;
	let visible = $state(false);
	let offResizeEvent: () => void;
	let closeButtonEl: HTMLElement;
	function updatePosition() {
		if (!relativeTo) {
			return;
		}
		computePosition(relativeTo, contentEl, {
			placement,
			middleware: [offset(relativeHeightOffset)],
		}).then(({ x, y }) => {
			Object.assign(contentEl.style, {
				left: x != null ? `${x}px` : '',
				top: y != null ? `${y}px` : '',
			});
		});
	}
	async function show() {
		if (dialog && !dialog.open) {
			if (relativeTo) {
				offResizeEvent = on(window, 'resize', updatePosition);
			}
			updatePosition();
			dialog.showModal();
			open = true;
			await tick();
			visible = true;
		}
	}
	function hide() {
		visible = false;
	}
	function close() {
		if (dialog?.open) {
			if (relativeTo) {
				offResizeEvent();
			}

			dialog.close();
			open = false;
			closeCallback?.();
		}
	}

	$effect(() => {
		open ? show() : hide();
		return () => {
			close();
		};
	});
</script>

<dialog
	class={['dialog-reset modal transition-opacity', classes, visible ? 'opacity-100' : 'opacity-0']}
	oncancel={hide}
	bind:this={dialog}
	ontransitionend={() => {
		if (!visible) {
			close();
		}
	}}
	style={extraStyle}
>
	<div class={['relative pane overflow-auto', paneClasses, { centered }]}>
		<div
			class={[contentDialogClasses, 'absolute flex flex-col items-end gap-2']}
			bind:this={contentEl}
		>
			{#if !hideCloseButton}
				<div bind:this={closeButtonEl}>
					{#if closeButtonSlot}
						{@render closeButtonSlot()}
					{:else}
						<Button
							variant="filled"
							color="white"
							size="md"
							class="z-100"
							text={closeText}
							icon="close"
							iconPosition="right"
							onclick={(e) => {
								closeButtonCallback?.(e);
								hide();
							}}
						/>
					{/if}
				</div>
			{/if}
			<div
				class="contents"
				use:clickOutside={{ disabled: !visible, notElements: closeButtonEl }}
				onClickOutside={(e) => {
					if (closeOnClickOutside) {
						clickOutsideCallback?.(e);
						hide();
					}
				}}
			>
				{@render children()}
			</div>
		</div>
	</div>
</dialog>

<style>
	.modal::backdrop {
		background-color: rgba(29, 49, 64, 0.5);
		backdrop-filter: blur(4px);
		/* hopefully prevents being able to scroll the background on mobile */
		pointer-events: none;
	}

	@media screen and (min-width: 960px) {
		.modal::backdrop {
			backdrop-filter: unset; /* no blur on desktop */
		}
	}
	.pane {
		height: 100dvh;
		width: 100dvw;

		position: relative;
	}
	.pane.centered {
		display: grid;
		place-items: center;
	}
</style>
